import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ReactFlow, Background, Controls, ReactFlowProvider, MarkerType } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { motion } from "framer-motion";
import {
  ArrowLeft, CheckCircle, XCircle, Play, Stop, ClockCountdown,
  User as UserIcon, CheckSquare, GitBranch, Lightning, BellSimple, Clock, PenNib, Sparkle, ListChecks,
  Gavel,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { GlassCard } from "@/components/glass/GlassCard";
import { PageShell } from "@/components/layout/AppLayout";
import api from "@/lib/api";
import { toast } from "sonner";

const ICON_MAP = {
  start: Play, end: Stop, approval: CheckSquare, decision: GitBranch,
  action: Lightning, notification: BellSimple, wait: Clock, sign: PenNib,
  ai_analyze: Sparkle, form: ListChecks,
};

function NodeCard({ data, selected }) {
  const Icon = ICON_MAP[data.type] || Lightning;
  const base = data.is_current ? "ring-2 ring-[var(--accent)] shadow-xl" :
               data.is_done ? "opacity-70" : "";
  return (
    <div className={`relative px-4 py-3 rounded-lg border min-w-[170px] glass-strong ${base}`} style={{ borderColor: data.is_current ? data.color : "var(--border-strong)" }}>
      {data.is_current && <div className="absolute -top-1.5 -end-1.5 w-3 h-3 rounded-full bg-[var(--accent)] animate-pulse" />}
      <div className="flex items-center gap-2 mb-1">
        <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ background: `${data.color}25`, border: `1px solid ${data.color}` }}>
          <Icon size={12} weight="fill" style={{ color: data.color }} />
        </div>
        <span className="text-[9px] uppercase tracking-widest font-semibold" style={{ color: data.color }}>{data.type.replace("_", " ")}</span>
        {data.is_done && <CheckCircle size={12} weight="fill" className="text-emerald-400 ms-auto" />}
      </div>
      <div className="text-sm font-semibold" style={{ fontFamily: 'var(--font-heading)' }}>{data.label}</div>
    </div>
  );
}

const nodeTypes = { custom: NodeCard };

function InstanceRunner() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [notes, setNotes] = useState("");
  const [acting, setActing] = useState(false);

  const load = () => api.get(`/instances/${id}`).then(({ data }) => setData(data));
  useEffect(() => { load(); }, [id]);

  if (!data) return <PageShell title="Instance"><div className="h-64 glass animate-pulse" /></PageShell>;

  const { instance, workflow } = data;
  const visitedNodeIds = new Set(instance.history.filter((h) => h.exited_at).map((h) => h.node_id));

  const flowNodes = (workflow?.nodes || []).map((n) => ({
    id: n.id,
    type: "custom",
    position: n.position || { x: 100, y: 100 },
    data: {
      label: n.label,
      type: n.type,
      color: n.color || "#38BDF8",
      is_current: n.id === instance.current_node_id && instance.status === "running",
      is_done: visitedNodeIds.has(n.id) && n.id !== instance.current_node_id,
    },
  }));

  const flowEdges = (workflow?.edges || []).map((e) => ({
    id: e.id,
    source: e.source,
    target: e.target,
    animated: e.source === instance.current_node_id,
    markerEnd: { type: MarkerType.ArrowClosed, color: "#38BDF8" },
    style: { stroke: visitedNodeIds.has(e.source) && visitedNodeIds.has(e.target) ? "#10B981" : "#38BDF8", strokeWidth: 2 },
  }));

  const currentNode = workflow?.nodes?.find((n) => n.id === instance.current_node_id);
  const outgoingEdges = (workflow?.edges || []).filter((e) => e.source === instance.current_node_id);
  const isDecision = currentNode?.type === "decision";

  const advance = async (action, branch) => {
    setActing(true);
    try {
      await api.post(`/instances/${id}/advance`, { action, notes, branch });
      toast.success(action === "approve" ? "Advanced" : "Rejected");
      setNotes("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setActing(false); }
  };

  const cancel = async () => {
    if (!confirm("Cancel this instance?")) return;
    await api.post(`/instances/${id}/cancel`);
    toast.success("Cancelled");
    load();
  };

  const currentHistory = instance.history.find((h) => h.node_id === instance.current_node_id && !h.exited_at);

  return (
    <PageShell
      title={instance.name}
      subtitle={`${workflow?.name || "—"} · v${instance.workflow_version}`}
      right={
        <>
          <Button variant="ghost" size="sm" onClick={() => nav("/app/workflows")} data-testid="inst-back-btn" className="gap-2"><ArrowLeft size={14} /> Back</Button>
          {instance.status === "running" && (
            <Button variant="ghost" size="sm" onClick={cancel} data-testid="inst-cancel-btn" className="text-[var(--danger)] gap-2">
              <XCircle size={14} /> Cancel
            </Button>
          )}
        </>
      }
    >
      {/* Status bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <GlassCard className="p-4">
          <div className="overline mb-1">Status</div>
          <div className="mt-1">
            <span className={`chip ${instance.status === "running" ? "chip-medium" : instance.status === "completed" ? "chip-low" : instance.status === "rejected" ? "chip-high" : "chip-neutral"}`}>{instance.status}</span>
          </div>
        </GlassCard>
        <GlassCard className="p-4">
          <div className="overline mb-1">Current stage</div>
          <div className="text-sm font-medium mt-1">{currentHistory?.node_label || instance.current_node_id}</div>
        </GlassCard>
        <GlassCard className="p-4">
          <div className="overline mb-1">Started</div>
          <div className="text-xs font-mono mt-1">{new Date(instance.started_at).toLocaleString()}</div>
        </GlassCard>
        <GlassCard className="p-4">
          <div className="overline mb-1">History steps</div>
          <div className="text-sm font-mono mt-1">{instance.history.length}</div>
        </GlassCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8">
          <GlassCard className="p-0 h-[60vh] overflow-hidden rounded-xl">
            <ReactFlow
              nodes={flowNodes}
              edges={flowEdges}
              nodeTypes={nodeTypes}
              nodesDraggable={false}
              nodesConnectable={false}
              edgesFocusable={false}
              fitView
              fitViewOptions={{ padding: 0.2 }}
              proOptions={{ hideAttribution: true }}
            >
              <Background color="var(--border)" gap={24} />
              <Controls showInteractive={false} className="!bg-[var(--surface-strong)] !border-[var(--border-strong)] !rounded-md" />
            </ReactFlow>
          </GlassCard>
        </div>

        <div className="lg:col-span-4 space-y-4">
          {instance.status === "running" && (
            <GlassCard strong className="p-5">
              <div className="overline mb-2">Action required</div>
              <div className="text-sm text-[var(--text-2)] mb-3">This instance is at <span className="font-semibold text-[var(--text)]">{currentHistory?.node_label}</span></div>
              <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes (optional)…" data-testid="inst-notes-input" className="glass-subtle border-[var(--border)] mb-3 text-sm" />
              {isDecision && outgoingEdges.length > 1 ? (
                <div className="space-y-2">
                  <div className="text-xs text-[var(--text-2)] mb-1">Choose branch:</div>
                  {outgoingEdges.map((e) => (
                    <Button key={e.id} onClick={() => advance("approve", e.label)} disabled={acting} data-testid={`inst-branch-${e.id}`} className="w-full justify-start gap-2" variant="outline">
                      <CheckCircle size={14} className="text-emerald-400" />
                      <span className="font-mono text-xs">{e.label || `to ${e.target}`}</span>
                    </Button>
                  ))}
                  <Button onClick={() => advance("reject")} disabled={acting} variant="outline" data-testid="inst-reject-btn" className="w-full gap-2 glass-subtle border-[var(--border-strong)] text-[var(--danger)]">
                    <XCircle size={14} /> Reject
                  </Button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Button onClick={() => advance("approve")} disabled={acting} data-testid="inst-approve-btn" className="flex-1 gap-2">
                    <CheckCircle size={14} weight="fill" /> Approve
                  </Button>
                  <Button onClick={() => advance("reject")} disabled={acting} variant="outline" data-testid="inst-reject-btn" className="flex-1 gap-2 glass-subtle border-[var(--border-strong)] text-[var(--danger)]">
                    <XCircle size={14} /> Reject
                  </Button>
                </div>
              )}
            </GlassCard>
          )}

          <GlassCard className="p-5">
            <div className="overline mb-3 flex items-center gap-2"><ClockCountdown size={12} /> Audit log</div>
            <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
              {instance.history.map((h, i) => {
                const Icon = ICON_MAP[h.node_type] || Lightning;
                const done = !!h.exited_at;
                return (
                  <div key={i} className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${done ? "bg-emerald-500/20 border-emerald-500/40" : "bg-amber-500/20 border-amber-500/40"} border`}>
                        <Icon size={12} weight="fill" className={done ? "text-emerald-400" : "text-amber-400"} />
                      </div>
                      {i < instance.history.length - 1 && <div className="w-px flex-1 bg-[var(--border)] my-1" />}
                    </div>
                    <div className="flex-1 pb-2">
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-medium">{h.node_label}</div>
                        <span className={`chip ${h.result === "approve" || h.result === "completed" ? "chip-low" : h.result === "reject" ? "chip-high" : "chip-medium"}`}>{h.result}</span>
                      </div>
                      <div className="text-[10px] text-[var(--text-3)] font-mono mt-0.5">{new Date(h.entered_at).toLocaleString()}</div>
                      {h.executor && <div className="text-xs text-[var(--text-2)] mt-1 flex items-center gap-1"><UserIcon size={10} /> {h.executor}</div>}
                      {h.notes && <div className="text-xs text-[var(--text-2)] mt-1 italic">"{h.notes}"</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </GlassCard>
        </div>
      </div>
    </PageShell>
  );
}

export default function WorkflowInstance() {
  return <ReactFlowProvider><InstanceRunner /></ReactFlowProvider>;
}
